<script setup lang="ts">
import { ref } from 'vue'
import {
  PBtnBadge,
  PLoader,
  PSpace,
  PSpaceSize,
  PSuggestionInputField,
  type PSuggestionInputFieldComponent,
  PTag,
  PTextFormBody,
  usePrevioDS,
} from '@previo/design-system'
import { Color, License, type PropertyId } from '@previo/codebooks'
import { $t } from '@previo/core'
import { useLatestRequest } from '~/composables/useLatestRequest'
import { usePropertySearchAPI } from '~/layouts/components/app-menu/property-search/http'
import type { PropertySearchItem } from '~/layouts/components/app-menu/property-search/http/types'
import { usePropertySearchNavigation } from '~/layouts/components/app-menu/property-search/composables/usePropertySearchNavigation'

const ds = usePrevioDS()
const searchAPI = usePropertySearchAPI()
const latestRequest = useLatestRequest()
const { propertyDetailUrl, propertyListUrl } = usePropertySearchNavigation()

const suggestionInputField = ref<PSuggestionInputFieldComponent | null>(null)
const keyword = ref<string | null>(null)
const loading = ref<boolean>(false)
const properties = ref<PropertySearchItem[]>([])
const selectedPropertyId = ref<PropertyId | null>(null)

const resetSearch = (): void => {
  latestRequest.invalidate()
  keyword.value = null
  properties.value = []
  loading.value = false
}

const search = (value: string | null): void => {
  keyword.value = value?.trim() || null

  if (keyword.value === null) {
    resetSearch()

    return
  }

  loading.value = true

  latestRequest
    .run(() => searchAPI.search(keyword.value ?? ''))
    .then((outcome) => {
      if (outcome.stale) {
        return
      }

      properties.value = outcome.value
      loading.value = false
    })
    .catch(() => {
      properties.value = []
      loading.value = false
    })
}

const openProperty = (value: PropertySearchItem | PropertyId | string | null): void => {
  if (typeof value !== 'number') {
    selectedPropertyId.value = null

    return
  }

  selectedPropertyId.value = value
  window.location.assign(propertyDetailUrl(value))
}

const openPropertyList = (value: string | null): void => {
  if (!value?.trim()) {
    return
  }

  window.location.assign(propertyListUrl(value))
}

const openMobileSearch = (): void => {
  resetSearch()
  suggestionInputField.value?.showSuggester()
}
</script>

<template>
  <p-btn-badge
    v-if="ds.screen().isMobile"
    :color="Color.WHITE"
    cy="open-property-search"
    icon-start="magnifying-glass"
    @click="openMobileSearch"
  />

  <div v-show="ds.screen().isLaptopAndLarge" class="crm-app-menu-property-search">
    <p-suggestion-input-field
      ref="suggestionInputField"
      :create="$t('Show in List of hotels')"
      create-end-icon="arrow-right"
      cy="property-search"
      :is-create-visible="keyword !== null"
      item-text="name"
      item-value="id"
      :items="properties"
      :loading="loading"
      menu-anchor="bottom end"
      menu-self="top end"
      :menu-width="480"
      :model-value="selectedPropertyId"
      :placeholder="$t('Search hotels')"
      @blur="resetSearch"
      @create="openPropertyList"
      @filter="search"
      @update:model-value="openProperty"
    >
      <template #loading>
        <p-loader :color="Color.PRIMARY" />
      </template>

      <template #item="{ item }: { item: PropertySearchItem }">
        <div class="p-flex p-no-wrap p-items-center p-flex-1 p-min-width-0">
          <p-text-form-body
            class="p-flex-1 p-min-width-0"
            :color="Color.GREY_600"
            :text="item.name"
            ellipsis
          />
          <p-space :mobile="PSpaceSize.SM" />
          <p-text-form-body :color="Color.GREY_500" :text="`[${item.id}]`" />
          <template v-if="item.townName">
            <p-space :mobile="PSpaceSize.SM" />
            <p-text-form-body :color="Color.GREY_500" :text="item.townName" />
          </template>
          <p-space :mobile="PSpaceSize.SM" />
          <p-tag
            :color="License.getById(item.licenseId).getColor()"
            :text="License.getById(item.licenseId).getShortcut()"
          />
        </div>
      </template>
    </p-suggestion-input-field>
  </div>
</template>

<style lang="scss" scoped>
.crm-app-menu-property-search {
  width: 280px;
}
</style>
